import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaTrash, FaSave, FaTimes, FaPlus, FaSearch } from "react-icons/fa";
import AdminNavbar from "../Layout/AdminNavbar";
import API_BASE_URL from "../config";

const S = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .tm-wrap {
    min-height: 100vh;
    background: #f0f2f8;
    font-family: 'Plus Jakarta Sans', sans-serif;
    padding-top: 108px;
  }

  /* ── TOP BANNER ── */
  .tm-banner {
    background: linear-gradient(120deg, #1e1b4b 0%, #3730a3 50%, #4f46e5 100%);
    padding: 36px 48px 80px;
    position: relative;
    overflow: hidden;
  }

  .tm-banner::before {
    content: '';
    position: absolute;
    top: -60px; right: -60px;
    width: 280px; height: 280px;
    border-radius: 50%;
    background: rgba(255,255,255,0.04);
  }

  .tm-banner::after {
    content: '';
    position: absolute;
    bottom: -80px; left: 30%;
    width: 360px; height: 360px;
    border-radius: 50%;
    background: rgba(255,255,255,0.03);
  }

  .tm-banner-title {
    font-size: 28px;
    font-weight: 800;
    color: #fff;
    letter-spacing: -0.6px;
  }

  .tm-banner-sub {
    margin-top: 6px;
    font-size: 14px;
    color: rgba(255,255,255,0.55);
    font-weight: 400;
  }

  .tm-stats-row {
    display: flex;
    gap: 16px;
    margin-top: 24px;
  }

  .tm-stat-pill {
    background: rgba(255,255,255,0.10);
    border: 1px solid rgba(255,255,255,0.14);
    border-radius: 40px;
    padding: 6px 16px;
    font-size: 13px;
    font-weight: 600;
    color: rgba(255,255,255,0.85);
    display: flex;
    align-items: center;
    gap: 7px;
    backdrop-filter: blur(6px);
  }

  .tm-stat-dot {
    width: 7px; height: 7px;
    border-radius: 50%;
    background: #818cf8;
  }

  /* ── FLOATING CONTENT PANEL ── */
  .tm-body {
    max-width: 1100px;
    margin: -44px auto 0;
    padding: 0 32px 60px;
    position: relative;
    z-index: 2;
  }

  /* ── ACTION BAR ── */
  .tm-actionbar {
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 4px 24px rgba(30,27,75,0.10);
    padding: 18px 22px;
    display: flex;
    gap: 12px;
    align-items: center;
    margin-bottom: 24px;
    flex-wrap: wrap;
  }

  .tm-search-wrap {
    flex: 1;
    min-width: 180px;
    position: relative;
  }

  .tm-search-icon {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #a5b4fc;
    font-size: 13px;
  }

  .tm-search {
    width: 100%;
    border: 1.5px solid #e8eaf6;
    border-radius: 10px;
    padding: 10px 14px 10px 38px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 13.5px;
    color: #1e1b4b;
    background: #f8f8fd;
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  .tm-search::placeholder { color: #c7c9e8; }

  .tm-search:focus {
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99,102,241,0.12);
    background: #fff;
  }

  .tm-add-row {
    display: flex;
    gap: 10px;
    align-items: center;
    flex-wrap: wrap;
  }

  .tm-new-input {
    border: 1.5px solid #e8eaf6;
    border-radius: 10px;
    padding: 10px 14px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 13.5px;
    color: #1e1b4b;
    background: #f8f8fd;
    outline: none;
    width: 320px;
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  .tm-new-input::placeholder { color: #c7c9e8; }

  .tm-new-input:focus {
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99,102,241,0.12);
    background: #fff;
  }

  .tm-btn-add {
    background: #4f46e5;
    color: #fff;
    border: none;
    border-radius: 10px;
    padding: 10px 20px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 13.5px;
    font-weight: 700;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 7px;
    white-space: nowrap;
    box-shadow: 0 3px 12px rgba(79,70,229,0.35);
    transition: all 0.18s;
  }

  .tm-btn-add:hover {
    background: #4338ca;
    box-shadow: 0 5px 18px rgba(79,70,229,0.45);
    transform: translateY(-1px);
  }

  /* ── CARD GRID ── */
  .tm-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 16px;
  }

  .tm-card {
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 2px 12px rgba(30,27,75,0.07);
    padding: 22px 20px 18px;
    display: flex;
    flex-direction: column;
    gap: 14px;
    border: 1.5px solid transparent;
    transition: box-shadow 0.2s, border-color 0.2s, transform 0.2s;
    animation: cardIn 0.3s ease both;
  }

  @keyframes cardIn {
    from { opacity: 0; transform: translateY(10px) scale(0.98); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }

  .tm-card:hover {
    box-shadow: 0 8px 28px rgba(79,70,229,0.13);
    border-color: #e0e1ff;
    transform: translateY(-2px);
  }

  .tm-card.editing {
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99,102,241,0.12);
  }

  .tm-card-icon {
    width: 46px;
    height: 46px;
    border-radius: 12px;
    background: linear-gradient(135deg, #eef2ff, #e0e7ff);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    font-weight: 800;
    color: #4f46e5;
    letter-spacing: -1px;
    flex-shrink: 0;
  }

  .tm-card-name {
    font-size: 15px;
    font-weight: 700;
    color: #1e1b4b;
    line-height: 1.3;
    word-break: break-word;
    overflow-wrap: break-word;
  }

  .tm-card-desc {
    font-size: 12px;
    color: #94a3b8;
    font-weight: 400;
    margin-top: 3px;
    word-break: break-word;
    overflow-wrap: break-word;
    line-height: 1.5;
  }

  .tm-card-edit-input {
    width: 100%;
    border: 1.5px solid #6366f1;
    border-radius: 8px;
    padding: 8px 11px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 13.5px;
    font-weight: 600;
    color: #1e1b4b;
    outline: none;
    box-shadow: 0 0 0 3px rgba(99,102,241,0.1);
    margin-bottom: 6px;
  }

  .tm-card-footer {
    display: flex;
    gap: 8px;
    padding-top: 10px;
    border-top: 1px solid #f0f1fb;
  }

  .tm-card-btn {
    flex: 1;
    border: none;
    border-radius: 8px;
    padding: 8px 0;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 12.5px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    transition: all 0.15s;
  }

  .tm-card-btn.edit-btn   { background: #f0f1ff; color: #4f46e5; }
  .tm-card-btn.edit-btn:hover   { background: #e0e3ff; }
  .tm-card-btn.del-btn    { background: #fff1f1; color: #ef4444; }
  .tm-card-btn.del-btn:hover    { background: #ffe0e0; }
  .tm-card-btn.save-btn   { background: #4f46e5; color: #fff; box-shadow: 0 2px 8px rgba(79,70,229,0.3); }
  .tm-card-btn.save-btn:hover   { background: #4338ca; }
  .tm-card-btn.cancel-btn { background: #f3f4f6; color: #6b7280; }
  .tm-card-btn.cancel-btn:hover { background: #e5e7eb; }

  /* ── EMPTY ── */
  .tm-empty {
    grid-column: 1 / -1;
    padding: 72px 0;
    text-align: center;
  }

  .tm-empty-emoji { font-size: 48px; margin-bottom: 14px; }
  .tm-empty-txt   { font-size: 15px; font-weight: 600; color: #818cf8; }
  .tm-empty-sub   { font-size: 13px; color: #c7c9e8; margin-top: 4px; }

  .tm-section-label {
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: #a5b4fc;
    margin-bottom: 14px;
  }

  @media (max-width: 640px) {
    .tm-banner { padding: 28px 20px 70px; }
    .tm-body   { padding: 0 14px 40px; }
    .tm-actionbar { flex-direction: column; align-items: stretch; }
    .tm-add-row   { flex-direction: column; }
    .tm-new-input { width: 100%; }
    .tm-banner-title { font-size: 22px; }
  }
`;

export default function TeamCRUD() {
  const [teams, setTeams] = useState([]);
  const [newTeam, setNewTeam] = useState({ name: "", description: "" });
  const [editingId, setEditingId] = useState(null);
  const [editTeam, setEditTeam] = useState({ name: "", description: "" });
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const fetchTeams = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/teams`, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      const data = await res.json();
      setTeams(data);
    } catch { toast.error("Failed to load teams."); }
  };

  useEffect(() => { fetchTeams(); }, []);

  useEffect(() => {
    const t = localStorage.getItem("token");
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");
    if (!t) { toast.error("You must be logged in to access this page."); navigate("/"); return; }
    if (!roles.includes("ROLE_ADMIN")) { toast.error("You are not authorized to access this page."); navigate("/"); }
  }, [navigate]);

  const handleAdd = async () => {
    if (!newTeam.name.trim()) { toast.warning("Please enter a team name."); return; }
    try {
      const res = await fetch(`${API_BASE_URL}/api/teams`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(newTeam),
      });
      if (res.ok) {
        toast.success("Team added successfully!");
        setNewTeam({ name: "", description: "" });
        fetchTeams();
      } else { toast.error("Description should be less than 255 characters"); }
    } catch { toast.error("Error adding team."); }
  };

  const handleUpdate = async (id) => {
    if (!editTeam.name.trim()) { toast.warning("Please enter a valid team name."); return; }
    try {
      const res = await fetch(`${API_BASE_URL}/api/teams/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(editTeam),
      });
      if (res.ok) {
        toast.success("Team updated!");
        setEditingId(null);
        setEditTeam({ name: "", description: "" });
        fetchTeams();
      } else { toast.error(await res.text()); }
    } catch { toast.error("Error updating team."); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this team?")) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/teams/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.info("Team deleted.");
        fetchTeams();
      } else { toast.error(await res.text()); }
    } catch { toast.error("Error deleting team."); }
  };

  const initials = (name) => name ? name.slice(0, 2).toUpperCase() : "??";

  const filtered = teams.filter(t =>
    t.name.toLowerCase().includes(search.toLowerCase()) ||
    (t.description && t.description.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <>
      <style>{S}</style>
      <div style={{ position: "fixed", top: 0, left: 0, width: "100%", zIndex: 1000 }}>
        <AdminNavbar />
      </div>

      <div className="tm-wrap">
        {/* Banner */}
        <div className="tm-banner">
          <div className="tm-banner-title">Team Management</div>
          <div className="tm-banner-sub">Create and manage all your teams from one place</div>
          <div className="tm-stats-row">
            <div className="tm-stat-pill">
              <span className="tm-stat-dot" />
              {teams.length} Teams
            </div>
            {search && (
              <div className="tm-stat-pill">
                <span className="tm-stat-dot" style={{ background: "#fbbf24" }} />
                {filtered.length} Results
              </div>
            )}
          </div>
        </div>

        <div className="tm-body">
          {/* Action Bar */}
          <div className="tm-actionbar">
            <div className="tm-search-wrap">
              <FaSearch className="tm-search-icon" />
              <input
                className="tm-search"
                placeholder="Search teams…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <div className="tm-add-row">
              <input
                className="tm-new-input"
                placeholder="Team name…"
                value={newTeam.name}
                onChange={e => setNewTeam({ ...newTeam, name: e.target.value })}
                onKeyDown={e => e.key === "Enter" && handleAdd()}
              />
              <input
                className="tm-new-input"
                placeholder="Description (optional)…"
                value={newTeam.description}
                onChange={e => setNewTeam({ ...newTeam, description: e.target.value })}
                onKeyDown={e => e.key === "Enter" && handleAdd()}
              />
              <button className="tm-btn-add" onClick={handleAdd}>
                <FaPlus size={11} /> Add
              </button>
            </div>
          </div>

          {/* Grid */}
          <div className="tm-section-label">All Teams</div>
          <div className="tm-grid">
            {filtered.length === 0 && (
              <div className="tm-empty">
                <div className="tm-empty-emoji">👥</div>
                <div className="tm-empty-txt">No teams found</div>
                <div className="tm-empty-sub">Try a different search or add one above</div>
              </div>
            )}
            {filtered.map((team) => (
              <div key={team.id} className={`tm-card${editingId === team.id ? " editing" : ""}`}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
                  <div className="tm-card-icon">{initials(team.name)}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {editingId === team.id ? (
                      <>
                        <input
                          className="tm-card-edit-input"
                          value={editTeam.name}
                          onChange={e => setEditTeam({ ...editTeam, name: e.target.value })}
                          placeholder="Team name"
                          autoFocus
                          onKeyDown={e => { if (e.key === "Escape") setEditingId(null); }}
                        />
                        <input
                          className="tm-card-edit-input"
                          value={editTeam.description}
                          onChange={e => setEditTeam({ ...editTeam, description: e.target.value })}
                          placeholder="Description"
                          onKeyDown={e => {
                            if (e.key === "Enter") handleUpdate(team.id);
                            if (e.key === "Escape") setEditingId(null);
                          }}
                        />
                      </>
                    ) : (
                      <>
                        <div className="tm-card-name">{team.name}</div>
                        {team.description && (
                          <div className="tm-card-desc">{team.description}</div>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <div className="tm-card-footer">
                  {editingId === team.id ? (
                    <>
                      <button className="tm-card-btn save-btn" onClick={() => handleUpdate(team.id)}>
                        <FaSave size={10} /> Save
                      </button>
                      <button className="tm-card-btn cancel-btn" onClick={() => {
                        setEditingId(null);
                        setEditTeam({ name: "", description: "" });
                      }}>
                        <FaTimes size={10} /> Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button className="tm-card-btn edit-btn"
                        onClick={() => {
                          setEditingId(team.id);
                          setEditTeam({ name: team.name, description: team.description || "" });
                        }}>
                        <FaEdit size={10} /> Edit
                      </button>
                      <button className="tm-card-btn del-btn" onClick={() => handleDelete(team.id)}>
                        <FaTrash size={10} /> Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}